import { useUpdateInvoice } from "@/queries/invoices/update";
import { ArrowRightLeft } from "lucide-react";
import { type PropsWithChildren, useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Shortcut } from "@/components/ui/shortcut";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TooltipContent } from "@/components/ui/tooltip";
import { TooltipTrigger } from "@/components/ui/tooltip";
import { Tooltip } from "@/components/ui/tooltip";
import { InvoiceModes } from "@/types/utils";

import { useInvoiceReviewContext } from "../ReviewProvider";

type ToolBarProps = PropsWithChildren<{
  disabled?: boolean;
  readOnly?: boolean;
}>;

export const ReviewToolBar = ({
  children,
  disabled = false,
  readOnly = false,
}: ToolBarProps) => {
  const { t } = useTranslation();
  const { invoiceReviewState } = useInvoiceReviewContext();
  const [updateInvoice] = useUpdateInvoice();
  const [invoiceMode, setInvoiceMode] = useState<InvoiceModes>();

  const { invoiceData } = invoiceReviewState;

  useEffect(() => {
    // Set invoiceMode based on invoiceData.isNettoInvoice when the component mounts or invoiceData changes
    if (invoiceData?.isNettoInvoice !== undefined) {
      setInvoiceMode(
        invoiceData.isNettoInvoice ? InvoiceModes.Netto : InvoiceModes.Brutto,
      );
    }
  }, [invoiceData]);

  useHotkeys<HTMLButtonElement>("m", () => {
    handleInvoiceModeChangeAndSave();
  });

  if (!invoiceData) return null;

  const handleInvoiceModeChangeAndSave = async () => {
    const newMode =
      invoiceMode === InvoiceModes.Netto
        ? InvoiceModes.Brutto
        : InvoiceModes.Netto;
    const isNettoInvoiceValue = newMode === InvoiceModes.Netto;
    await updateInvoice({
      invoiceId: invoiceData.invoiceId,
      isNettoInvoice: isNettoInvoiceValue,
    });
  };

  return (
    <div className="flex flex-row justify-end gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Button
              size="sm"
              variant="outline"
              onClick={handleInvoiceModeChangeAndSave}
              disabled={disabled || readOnly}
            >
              <ArrowRightLeft width={16} height={16} className="mr-2" />
              {t(
                invoiceMode === "Netto"
                  ? "switch_to_brutto_mode"
                  : "switch_to_netto_mode",
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <Shortcut.ShortcutRoot>
              <Shortcut.ShortcutHotKey className="bg-muted-foreground">
                M
              </Shortcut.ShortcutHotKey>
            </Shortcut.ShortcutRoot>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {children}
    </div>
  );
};
